/**
 * 取引契約一覧 → 48点データ画面 (`/supply/48data`) への遷移 URL builder (純関数)。
 *
 * spec `bilateral-trade-list.md` AC-010 / AC-012 に基づき、 行内「48点データへ」ボタン /
 * 手入力取引閲覧モーダル footer から該当取引のコンテキスト (受渡日 / 契約ID / エリア /
 * 売買区分 / 相対先 / 数量) をクエリパラメータで引き渡す。 param 名は `supply-48-data.md`
 * AC-019 (date / contract / filterArea / buysell / counterparty / volumeMw) と揃える。
 *
 * design 梅版 `design/supply/bilateral.html` の `__biBuild48Url` を app 実装に移植したもの。
 * [mock差異] design は `filterArea` / `buysell` を日本語表示名 ('東京' / '買') で渡すが、
 * 本実装は受け側 (Supply48Data) が解釈できる enum 数値で渡す (JP 名 → enum の脆い
 * マッピングを避ける)。 date は design が常に「今日」を渡すのに対し、 本実装は取引の
 * 受渡開始日 (deliveryPeriodFrom) を渡す (= 契約が有効な受渡日に着地させ AC-012 の
 * 「即座に 48コマ値を編集できる状態」 の意図を満たす)。 受渡開始日が無ければ date を
 * 省略し、 受け側は今日にフォールバックする。
 *
 * 受け側が実際に初期フィルタへ反映するのは date / filterArea / buysell の 3 つ
 * (`Supply48Data/models/supply48QueryParams.ts` の `parseSupply48InitialFilters`
 * 参照)。 counterparty / volumeMw は spec AC-012 のコンテキスト受け渡し用に載せるが、
 * 対応フィルタが無いため初期表示には反映されない (= 情報の欠落を防ぐ carry-through)。
 * contract (取引ID) だけは別目的で受け側が消費する: AC-S4D-052 の遷移直後の自動横
 * スクロール・一時ハイライト対象の識別子として `parseSupply48ScrollTarget` が読む
 * (フィルタには反映しない)。
 */
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'

import { WORKFLOW_ID_PARAM } from '~/models/workflowIdParam'
import { routePaths } from '~/routes/routePaths'

type CivilDate = { year: number; month: number; day: number }

export type Supply48LinkInput = {
  /** エリアコード。 UNSPECIFIED の場合は filterArea を付与しない。 */
  areaCode: AreaCode
  /** 売買区分。 UNSPECIFIED の場合は buysell を付与しない。 */
  buySell: Side
  /** 受渡開始日。 date パラメータの source (未設定なら date を省略)。 */
  deliveryPeriodFrom?: CivilDate | undefined
  /** 成約ID (例: BL-TK001-01)。 contract パラメータ。 */
  publicTradeId: string
  /** 相対先名。 counterparty パラメータ。 */
  partnerName: string
  /** 数量 (MW) の文字列 (未設定なら空文字)。 volumeMw パラメータ。 */
  volumeMw: string
  /** ワークフローケース ID。遷移先に read-only コンテキストを引き継ぐ。 */
  caseId?: string | undefined
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0')
}

function toIsoDate(d: CivilDate): string {
  return `${d.year}-${pad2(d.month)}-${pad2(d.day)}`
}

/**
 * 取引コンテキストから 48点データ画面への遷移 path (query 付き) を組み立てる。
 * navigate() にそのまま渡せる `/supply/48data?...` を返す。
 */
export function buildSupply48Url(input: Supply48LinkInput): string {
  const params = new URLSearchParams()

  if (input.deliveryPeriodFrom !== undefined) {
    params.set('date', toIsoDate(input.deliveryPeriodFrom))
  }
  if (input.areaCode !== AreaCode.UNSPECIFIED) {
    // 受け側が解釈する enum 数値で渡す ([mock差異] 参照)。
    params.set('filterArea', String(input.areaCode))
  }
  if (input.buySell !== Side.UNSPECIFIED) {
    params.set('buysell', String(input.buySell))
  }
  // spec AC-012 のコンテキスト carry-through (受け側フィルタには未反映)。
  if (input.publicTradeId !== '') {
    params.set('contract', input.publicTradeId)
  }
  if (input.partnerName !== '') {
    params.set('counterparty', input.partnerName)
  }
  if (input.volumeMw !== '') {
    params.set('volumeMw', input.volumeMw)
  }
  // 戻り導線用 (48点データ画面が対応した場合の戻り先ヒント)。
  params.set('returnTo', 'bilateral')
  if (input.caseId !== undefined && input.caseId !== '') {
    params.set(WORKFLOW_ID_PARAM, input.caseId)
  }

  return `${routePaths.supply48Data.path}?${params.toString()}`
}
